/**
 * Everybody using this, and what state they are in. F-81.
 *
 * One table, one row per learner, every column a thing the operator would otherwise
 * have to ask a person or an AWS CLI: what they are learning, whether a question is
 * open, whether they are paused, when they last said anything, and what they have
 * cost. It is deliberately dense — this is the screen he opens to answer "is anyone
 * stuck", and a card-per-learner layout would answer it one scroll at a time.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isError, listLearners, type AdminLearner, type AdminUsage, type ApiError } from './api';
import { Failed, Id, Nil, Waiting, abbreviate, ago, dollars, localTitle, stamp } from './parts';
import { answeringLabel, emailNote, reachLabel, reachRank } from './reach';

/**
 * What a web search costs, from docs/costs.md: `$10/1,000`.
 *
 * The only part of model spend this screen can price honestly. The usage counters
 * are aggregates across every model the account touched — Opus research, Sonnet item
 * generation, Haiku grading — and a token is a different price in each, so turning
 * `inputTokens` into dollars here would mean inventing a blended rate and printing
 * it as though it were measured. Searches are a flat per-search charge whatever the
 * model, so they are exact.
 *
 * The gap is real and it is F-81's "month-to-date model spend": see the report. It
 * closes with per-model counters, on the API side, not with arithmetic here.
 */
const DOLLARS_PER_SEARCH = 0.01;

/** A counter that may not have been written yet. Zero is the honest reading of absent. */
const counted = (value: number | undefined) => (typeof value === 'number' ? value : 0);

/** Tokens, all six counters, as one number worth glancing at plus the breakdown on hover. */
function Usage({ usage }: { usage: AdminUsage | undefined }) {
  if (!usage) return <Nil />;
  const parts: [string, number][] = [
    ['in', counted(usage.inputTokens)],
    ['out', counted(usage.outputTokens)],
    ['cache read', counted(usage.cacheReadTokens)],
    ['cache write', counted(usage.cacheWriteTokens)],
    ['batch in', counted(usage.batchInputTokens)],
    ['batch out', counted(usage.batchOutputTokens)],
  ];
  const total = parts.reduce((sum, [, value]) => sum + value, 0);
  const searches = counted(usage.webSearches);

  return (
    <span
      className="stack"
      title={parts.map(([label, value]) => `${label} ${value.toLocaleString('en')}`).join('\n')}
    >
      <span className="num">{abbreviate(total)} tok</span>
      <span className="sub">
        {counted(usage.calls)} calls · {searches} searches
        {searches > 0 && ` ≈ ${dollars(searches * DOLLARS_PER_SEARCH)}`}
      </span>
    </span>
  );
}

/*
 * The phone column and its reveal button are gone with F-125.
 *
 * They showed `••••••2423` with one logged, deliberate way to see the digits, which was right
 * while a number was how somebody was reached. F-103 retired WhatsApp on 2026-09-16 and left
 * `phone` on the row as a vestige that nothing reads — so the column showed a masked value
 * that meant nothing and offered to disclose a personal identifier for no purpose. The route
 * behind it (`GET /api/admin/phone`) went too.
 */

/**
 * How we reach them, or that we cannot. F-125.
 *
 * **The loudest cell on the screen, on purpose.** Since F-103 a learner with no push device
 * and no mailable address is composed questions, has items claimed and reviews scheduled, and
 * is taught nothing — and from every other column here that is indistinguishable from somebody
 * who is simply quiet. The wording and the severity are decided in `reach.ts`, which is
 * DOM-free and tested, because a label that read "email" when the honest answer was "nothing"
 * would hide exactly that.
 */
function Reach({ learner }: { learner: AdminLearner }) {
  const label = reachLabel(learner.reach);
  const note = emailNote(learner.reach);
  return (
    <span className="stack">
      <span className={`tag reach-${label.tone}`}>{label.text}</span>
      <span className="sub">{label.detail}</span>
      {note && <span className="sub warn">{note}</span>}
    </span>
  );
}

/** Whether they are answering what we send, over the last sixty of their own days. */
function Answering({ learner }: { learner: AdminLearner }) {
  const label = answeringLabel(learner.answering);
  return (
    <span className="stack">
      <span className={`num reach-${label.tone}`}>{label.text}</span>
      <span className="sub">{label.detail}</span>
      {learner.answering?.lastAnsweredDay && (
        <span className="sub">last answered {learner.answering.lastAnsweredDay}</span>
      )}
    </span>
  );
}

/** Whether a question is out and unanswered, and for how long — the stuck-learner column. */
function Open({ learner }: { learner: AdminLearner }) {
  if (!learner.openItemId) {
    // Paused is why nothing is open, and saying so here stops "none open" being read as
    // healthy on a learner nothing will ever send to again.
    if (learner.paused) {
      return (
        <span className="sub">
          {learner.autoPaused ? 'auto-paused — no answers' : 'paused by the learner'}
        </span>
      );
    }
    return <span className="sub">none open</span>;
  }
  return (
    <span className="stack">
      <Id value={learner.openItemId} />
      <span className="sub">open {ago(learner.openSince)}</span>
      {learner.paused && (
        <span className="sub warn">
          {learner.autoPaused ? 'auto-paused' : 'paused'} — nothing more will go out
        </span>
      )}
    </span>
  );
}

export default function Learners() {
  const [learners, setLearners] = useState<AdminLearner[]>();
  const [error, setError] = useState<ApiError>();

  useEffect(() => {
    let stale = false;
    void listLearners().then((result) => {
      if (stale) return;
      if (isError(result)) setError(result);
      else setLearners(result.learners);
    });
    return () => {
      stale = true;
    };
  }, []);

  if (error) return <Failed error={error} what="Learners" />;
  if (!learners) return <Waiting what="learners" />;

  /*
   * Unreachable first, then email-only, then push. F-125.
   *
   * The sort is the screen's opinion about what the operator is here to do: a learner nothing
   * can reach is work, and a dense table sorted by nothing in particular buries them at
   * whatever position the `Scan` happened to return. Ties keep the API's order, which is
   * stable across reloads because `listLearners` pages deterministically.
   */
  const ordered = [...learners].sort(
    (a, b) => reachRank(a.reach?.via) - reachRank(b.reach?.via),
  );
  const unreachable = learners.filter((learner) => learner.reach?.unreachable).length;

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h1>Learners</h1>
        <p className="sub">
          {learners.length} rows
          {unreachable > 0 && (
            <>
              {' · '}
              <span className="bad">{unreachable} unreachable</span>
            </>
          )}
        </p>
      </header>

      {learners.length === 0 ? (
        <p className="admin-empty">Nobody has signed in yet.</p>
      ) : (
        <div className="admin-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Learner</th>
                <th>Signed up</th>
                <th>Reach</th>
                <th>Objective</th>
                <th>Answering</th>
                <th>Window</th>
                <th>Open</th>
                <th>Reminders</th>
                <th>Last in</th>
                <th>Last out</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((learner) => (
                <tr
                  key={learner.userId}
                  className={
                    learner.reach?.unreachable
                      ? 'is-unreachable'
                      : learner.paused
                        ? 'is-paused'
                        : undefined
                  }
                >
                  <td>
                    <span className="stack">
                      <Link className="admin-link strong" to={`/admin/learner/${learner.userId}`}>
                        {learner.name || '(no name)'}
                      </Link>
                      <Id value={learner.userId} />
                      <span className="sub">
                        {learner.locale} · {learner.timezone}
                      </span>
                      {learner.paused && (
                        <span className="tag warn">
                          {learner.autoPaused ? 'auto-paused' : 'paused'}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="num" title={learner.createdAt || undefined}>
                    <span className="stack">
                      <span>{stamp(learner.createdAt)}</span>
                      <span className="sub">{ago(learner.createdAt)}</span>
                    </span>
                  </td>
                  <td>
                    <Reach learner={learner} />
                  </td>
                  <td>
                    {learner.objective ? (
                      <span className="stack">
                        <Link
                          className="admin-link"
                          to={`/admin/objective/${learner.objective.objectiveId}`}
                        >
                          {localTitle(learner.objective.title)}
                        </Link>
                        <span className="sub">
                          <span className={`tag status-${learner.objective.status}`}>
                            {learner.objective.status}
                          </span>{' '}
                          {/* Where the plan was built from (F-118, F-119). `research` is also
                              what an objective written before F-118 reads as, resolved by the
                              API rather than guessed at here. */}
                          <span className="tag">{learner.objective.planFrom ?? 'research'}</span>{' '}
                          {learner.objective.activatedAt
                            ? `started ${ago(learner.objective.activatedAt)}`
                            : 'never started'}
                        </span>
                      </span>
                    ) : (
                      <span className="sub">no objective</span>
                    )}
                  </td>
                  <td>
                    <Answering learner={learner} />
                  </td>
                  <td>
                    {learner.window ? (
                      <span className="stack">
                        <span className="num">
                          {learner.window.reachFrom}–{learner.window.reachUntil}
                        </span>
                        <span className="sub">
                          {learner.window.dailyLimit}/day
                          {learner.window.explanations ? ' · explanations' : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="sub">unset</span>
                    )}
                  </td>
                  <td>
                    <Open learner={learner} />
                  </td>
                  <td className="num">{learner.reminders}</td>
                  <td className="num" title={learner.lastInboundAt ?? undefined}>
                    {ago(learner.lastInboundAt)}
                  </td>
                  <td className="num" title={learner.lastOutboundAt ?? undefined}>
                    {ago(learner.lastOutboundAt)}
                  </td>
                  <td>
                    <Usage usage={learner.usage} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
